import { useState, useEffect } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Loader2,
  Wifi,
  WifiOff,
  AlertCircle,
  CheckCircle,
  RefreshCw,
  Database,
  Settings,
  User,
  Shield
} from "lucide-react";

// All theme colors now come from CSS custom properties via the palette system.

interface LoadingStateProps {
  className?: string;
}

interface AuthLoadingProps extends LoadingStateProps {
  status: 'authenticating' | 'initializing' | 'migrating' | 'syncing' | 'fallback';
  isFirebaseConfigured: boolean;
  migrationStatus?: {
    completed: boolean;
    migrated: string[];
    errors: string[];
  };
}

interface ConnectionStatusProps extends LoadingStateProps {
  status: 'connecting' | 'connected' | 'disconnected' | 'error';
  onRetry?: () => void;
  latency?: number;
  lastConnected?: Date;
}

interface ChatLoadingProps extends LoadingStateProps {
  type: 'connection' | 'messages' | 'rooms' | 'translation';
  message?: string;
}

export function AuthLoading({
  status,
  isFirebaseConfigured,
  migrationStatus,
  className = ""
}: AuthLoadingProps) {
  const getStatusInfo = () => {
    switch (status) {
      case 'migrating':
        return {
          icon: <Database className="w-8 h-8 text-[var(--accent)]" />,
          title: "Upgrading Your Data",
          description: migrationStatus ?
            `Migrating your settings to the latest version...` :
            "Preparing your app data for the latest features...",
        
        };
      case 'authenticating':
        return {
          icon: <Shield className="w-8 h-8 text-[var(--accent)]" />,
          title: "Signing You In",
          description: "Setting up your secure anonymous session...",
         
        };
      case 'syncing':
        return {
          icon: <User className="w-8 h-8 text-[var(--accent)]" />,
          title: "Syncing Your Profile",
          description: "Connecting your settings with our servers...",
          
        };
      case 'initializing':
        return {
          icon: <Settings className="w-8 h-8 text-[var(--accent)]" />,
          title: "Setting Up Vyzle Chat",
          description: "Preparing your personalized chat experience...",
         
        };
      case 'fallback':
        return {
          icon: <Loader2 className="w-8 h-8 text-[var(--accent)] animate-spin" />,
          title: "Restoring Session",
          description: "Reconnecting to your account...",
          
        };
      default:
        return {
          icon: <Loader2 className="w-8 h-8 text-[var(--accent)]" />,
          title: "Loading",
          description: "Please wait...",
       
        };
    }
  };

  const statusInfo = getStatusInfo();

  return (
    <div

      className={`min-h-screen flex items-center justify-center p-4 font-sans transition-colors duration-500
        dark:text-white text-gray-900
        bg-[var(--chat-bg)]
        ${className}`}
    >
      <Card className="w-full max-w-md shadow-xl border-0 bg-white/80 dark:bg-[#0f172a]  backdrop-blur-sm">
        <CardContent className="pt-8 pb-6">
          <div className="text-center space-y-6">
            {/* Status Icon */}
            <div className={`w-16 h-16 bg-[var(--accent)] dark:bg-[var(--accent)] rounded-full flex items-center justify-center mx-auto`}>
              {status === 'authenticating' || status === 'initializing' || status === 'syncing' ? (
                <Loader2 className={`w-8 h-8 text-[#f8fafc] animate-spin`} />
              ) : (
                statusInfo.icon
              )}
            </div>

            {/* Status Text */}
            <div className="space-y-2">
              <h3 className="text-xl font-semibold text-foreground">
                {statusInfo.title}
              </h3>
              <p className="text-muted-foreground">
                {statusInfo.description}
              </p>
            </div>

            {/* Migration Progress */}
            {status === 'migrating' && migrationStatus && (
              <div className="space-y-3">
                <div className="flex items-center justify-between text-sm">
                  <span className="text-muted-foreground">Migration Progress</span>
                  <Badge variant={migrationStatus.completed ? "default" : "secondary"}>
                    {migrationStatus.completed ? "Complete" : "In Progress"}
                  </Badge>
                </div>

                {migrationStatus.migrated.length > 0 && (
                  <div className="text-left space-y-1">
                    <p className="text-sm font-medium text-foreground">Completed:</p>
                    <ul className="text-xs text-muted-foreground space-y-1">
                      {migrationStatus.migrated.map((item, index) => (
                        <li key={index} className="flex items-center space-x-2">
                          <CheckCircle className="w-3 h-3 text-[var(--accent)] flex-shrink-0" />
                          <span>{item}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {migrationStatus.errors.length > 0 && (
                  <Alert>
                    <AlertCircle className="h-4 w-4" />
                    <AlertDescription>
                      Some settings couldn't be migrated. The app will use defaults.
                    </AlertDescription>
                  </Alert>
                )}
              </div>
            )}

            {/* Firebase Status */}
            {!isFirebaseConfigured && (
              <Alert>
                <AlertCircle className="h-4 w-4" />
                <AlertDescription className="text-left">
                  Firebase is not configured. Some features may be limited.
                </AlertDescription>
              </Alert>
            )}

            {/* Progress Indicator */}
            <div className="flex justify-center space-x-2">
              <div className={`w-2 h-2 rounded-full ${status === 'migrating' ? 'bg-[var(--accent)]' : 'bg-gray-300'}`} />
              <div className={`w-2 h-2 rounded-full ${status === 'authenticating' ? 'bg-[var(--accent)]' : 'bg-gray-300'}`} />
              <div className={`w-2 h-2 rounded-full ${status === 'syncing' ? 'bg-[var(--accent)]' : 'bg-gray-300'}`} />
              <div className={`w-2 h-2 rounded-full ${status === 'initializing' ? 'bg-[var(--accent)]' : 'bg-gray-300'}`} />
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

export function ConnectionStatus({
  status,
  onRetry,
  latency,
  lastConnected,
  className = ""
}: ConnectionStatusProps) {
  // Compact LED-style indicator only
  const getColor = () => {
    switch (status) {
      case 'connected':
        return 'bg-[var(--accent)]';
      case 'connecting':
        return 'bg-yellow-500 animate-pulse';
      case 'error':
        return 'bg-red-600';
      case 'disconnected':
      default:
        return 'bg-red-500';
    }
  };

  return (
    <div className={`fixed bottom-3 right-3 z-50 ${className}`} aria-label="Connection status" title={status}>
      <div className="flex items-center gap-2 p-2 rounded-full bg-background/80 border border-border shadow-md">
        <div className={`w-2.5 h-2.5 rounded-full ${getColor()}`} />
        {latency !== undefined && status === 'connected' && (
          <span className="text-[10px] text-muted-foreground">{latency}ms</span>
        )}
        {(status === 'error' || status === 'disconnected') && onRetry && (
          <button aria-label="Retry connection" onClick={onRetry} className="text-[10px] underline text-muted-foreground">
            Retry
          </button>
        )}
      </div>
    </div>
  );
}

export function ChatLoading({ type, message, className = "" }: ChatLoadingProps) {
  const getLoadingInfo = () => {
    switch (type) {
      case 'connection':
        return {
          icon: <Wifi className="w-8 h-8" />,
          title: "Connecting to Chat",
          description: message || "Establishing secure connection...",
        };
      case 'messages':
        return {
          icon: <Loader2 className="w-8 h-8 animate-spin" />,
          title: "Loading Messages",
          description: message || "Fetching your conversation history...",
        };
      case 'rooms':
        return {
          icon: <Loader2 className="w-8 h-8 animate-spin" />,
          title: "Loading Conversations",
          description: message || "Getting your chat rooms...",
        };
      case 'translation':
        return {
          icon: <Loader2 className="w-8 h-8 animate-spin" />,
          title: "Translating Message",
          description: message || "Converting to your language...",
        };
      default:
        return {
          icon: <Loader2 className="w-8 h-8 animate-spin" />,
          title: "Loading",
          description: message || "Please wait...",
        };
    }
  };

  const loadingInfo = getLoadingInfo();

  return (
    <div

      className={`min-h-screen flex items-center justify-center p-4 font-sans transition-colors duration-500
        dark:text-white text-gray-900
        bg-[var(--chat-bg)]
        ${className}`}
    >
      {/* <div className={`flex-1 flex items-center justify-center p-8 ${className}`}> */}
      <div className="text-center space-y-4 max-w-sm mx-auto">
        <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto">
          {loadingInfo.icon}
        </div>
        <div className="space-y-2">
          <h3 className="text-lg font-semibold text-foreground">
            {loadingInfo.title}
          </h3>
          <p className="text-muted-foreground">
            {loadingInfo.description}
          </p>
        </div>
      </div>
    </div>
  );
}

// Enhanced Error Alert Component
interface ErrorAlertProps extends LoadingStateProps {
  error: string;
  onRetry?: () => void;
  onDismiss?: () => void;
  type?: 'connection' | 'auth' | 'data' | 'general';
}

export function ErrorAlert({
  error,
  onRetry,
  onDismiss,
  type = 'general',
  className = ""
}: ErrorAlertProps) {
  const getErrorInfo = () => {
    switch (type) {
      case 'connection':
        return {
          icon: <WifiOff className="h-4 w-4" />,
          title: "Connection Error",
          suggestions: [
            "Check your internet connection",
            "Try refreshing the page",
            "Check if the server is accessible"
          ]
        };
      case 'auth':
        return {
          icon: <Shield className="h-4 w-4" />,
          title: "Authentication Error",
          suggestions: [
            "Clear browser cache and cookies",
            "Try incognito/private browsing",
            "Check Firebase configuration"
          ]
        };
      case 'data':
        return {
          icon: <Database className="h-4 w-4" />,
          title: "Data Error",
          suggestions: [
            "Try refreshing the page",
            "Clear local storage",
            "Contact support if persistent"
          ]
        };
      default:
        return {
          icon: <AlertCircle className="h-4 w-4" />,
          title: "Error",
          suggestions: []
        };
    }
  };

  const errorInfo = getErrorInfo();

  return (
    <Alert variant="destructive" className={className}>
      {errorInfo.icon}
      <div className="flex-1">
        <div className="flex items-center justify-between">
          <h4 className="font-medium">{errorInfo.title}</h4>
          {onDismiss && (
            <Button
              variant="ghost"
              size="sm"
              onClick={onDismiss}
              className="h-6 w-6 p-0"
            >
              ×
            </Button>
          )}
        </div>
        <AlertDescription className="mt-2">
          <p className="mb-3">{error}</p>

          {errorInfo.suggestions.length > 0 && (
            <div>
              <p className="font-medium mb-2">Try these solutions:</p>
              <ul className="space-y-1 text-sm">
                {errorInfo.suggestions.map((suggestion, index) => (
                  <li key={index} className="flex items-start space-x-2">
                    <span className="text-muted-foreground">•</span>
                    <span>{suggestion}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {onRetry && (
            <Button
              variant="outline"
              size="sm"
              onClick={onRetry}
              className="mt-3"
            >
              <RefreshCw className="w-4 h-4 mr-2" />
              Try Again
            </Button>
          )}
        </AlertDescription>
      </div>
    </Alert>
  );
}

// Progress Indicator Component
interface ProgressIndicatorProps {
  steps: string[];
  currentStep: number;
  className?: string;
}

export function ProgressIndicator({ steps, currentStep, className = "" }: ProgressIndicatorProps) {
  return (
    <div className={`w-full ${className}`}>
      <div className="flex justify-between mb-2">
        {steps.map((step, index) => (
          <div
            key={index}
            className={`flex items-center space-x-2 ${index <= currentStep ? "text-primary" : "text-muted-foreground"
              }`}
          >
            <div
              className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-medium ${index < currentStep
                  ? "bg-primary text-primary-foreground"
                  : index === currentStep
                    ? "border-2 border-primary text-primary"
                    : "border-2 border-muted-foreground text-muted-foreground"
                }`}
            >
              {index < currentStep ? "✓" : index + 1}
            </div>
            <span className="text-xs hidden sm:block">{step}</span>
          </div>
        ))}
      </div>
      <div className="w-full bg-muted rounded-full h-2">
        <div
          className="bg-primary h-2 rounded-full transition-all duration-300"
          style={{ width: `${((currentStep + 1) / steps.length) * 100}%` }}
        />
      </div>
    </div>
  );
} 